import { useState } from 'react';

import {
  faArrowRightArrowLeft,
  faBuilding,
  faCircleCheck,
  faFileContract,
  faFileLines,
  faStar,
  faTrash,
  faTruck,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';

import { List, ListItem } from '@/components/List';
import { Typography } from '@/components/Typography';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFavoritesNavigation } from '@/data/hooks/ui/useFavoritesNavigation';
import type { FavoriteSearch } from '@/data/stores/favorites.store';

const SECTION_ICONS: Record<FavoriteSearch['type'], IconDefinition> = {
  client: faBuilding,
  contracts: faFileContract,
  orders: faTruck,
  resources: faUser,
  heading_clients: faFileLines,
  document_mapping: faArrowRightArrowLeft,
};

/** Menu « Mes recherches » de l'en-tête : favoris par section, sélection et suppression. */
export const FavoritesMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const {
    favorites,
    sections,
    isSelected,
    hasSelectedFavorite,
    selectFavorite,
    removeFavorite,
    getSummary,
  } = useFavoritesNavigation();

  if (favorites.length === 0) {
    return null;
  }

  const handleSelect = (favorite: FavoriteSearch) => {
    selectFavorite(favorite);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id='favoritesMenuButton'
          variant='ghost'
          size='icon'
          className='relative'
          aria-label={t('dashboard.pixid.favorites.title')}
        >
          <FontAwesomeIcon
            icon={hasSelectedFavorite ? faStarSolid : faStar}
            className={hasSelectedFavorite ? 'text-primary' : undefined}
          />
          <Badge className='absolute -top-1 -right-1 h-4 min-w-4 justify-center px-1 text-[10px] leading-none'>
            {favorites.length}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-96 p-0' data-testid='favorites-menu'>
        <div className='flex flex-col gap-1 border-b px-4 py-3'>
          <Typography variant='h4'>{t('dashboard.pixid.favorites.title')}</Typography>
          <Typography variant='paragraphSmall'>
            {t('dashboard.pixid.favorites.description', { count: favorites.length })}
          </Typography>
        </div>
        <div className='flex max-h-96 flex-col gap-3 overflow-y-auto p-2'>
          {sections.map((section) => (
            <div key={section.type} className='flex flex-col gap-1'>
              <Typography variant='paragraphMiniBold' className='px-2 pt-1'>
                {t(section.titleKey)}
              </Typography>
              <List className='flex flex-col gap-0.5'>
                {section.items.map((favorite) => {
                  const { title, subtitle, fullText } = getSummary(favorite);
                  const selected = isSelected(favorite);
                  return (
                    <ListItem key={favorite.id} className='flex items-center gap-1'>
                      <Button
                        variant='ghost'
                        className='h-auto min-w-0 flex-1 justify-start py-2'
                        title={fullText}
                        onClick={() => handleSelect(favorite)}
                      >
                        <FontAwesomeIcon
                          icon={SECTION_ICONS[section.type]}
                          className='text-muted-foreground'
                        />
                        <span className='flex min-w-0 flex-1 flex-col items-start text-left'>
                          <Typography
                            as='span'
                            variant='paragraphSmallMedium'
                            color='foreground'
                            className='w-full truncate'
                          >
                            {title}
                          </Typography>
                          {subtitle ? (
                            <Typography as='span' variant='paragraphMini' className='w-full truncate'>
                              {subtitle}
                            </Typography>
                          ) : null}
                        </span>
                        {selected ? (
                          <FontAwesomeIcon icon={faCircleCheck} className='text-primary' />
                        ) : null}
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon-sm'
                        aria-label={t('dashboard.pixid.favorites.remove')}
                        onClick={() => removeFavorite(favorite.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </ListItem>
                  );
                })}
              </List>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
